/* eslint-disable no-unused-vars */
import React, { useCallback } from 'react';
import Modal from '@material-ui/core/Modal';
import {
  Box,
  Button,
  Card,
  CardHeader,
  LinearProgress,
  makeStyles,
  Typography
} from '@material-ui/core';
import { TransactionPayment } from 'src/types';
import ScrollBar from 'react-perfect-scrollbar';
import { downloadCSV, formatCurrency } from 'src/utils';
import { ConsumedVoucherDetails } from 'src/redux/slices/voucher/types';
import { useNavigate } from 'react-router';
import { useSnackBar } from 'src/hooks';

const useStyles = makeStyles((theme) => ({
  body: {
    position: 'absolute',
    width: '90%',
    maxWidth: 1000,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    borderRadius: 8,
    top: '20%',
    [theme.breakpoints.down('xs')]: {
      top: '10%',
      padding: theme.spacing(2)
    },
    boxSizing: 'border-box'
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  footer: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  formContainer: {
    marginTop: theme.spacing(2),
    maxHeight: '400px'
  },
  footerBtn: {
    minWidth: 120
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    padding: theme.spacing(1)
  }
}));

interface Props {
  visible: boolean;
  onHandleClose: () => void;
  data?: TransactionPayment;
  title?: string;
  loading?: boolean;
  consumedVouchersList?: ConsumedVoucherDetails[];
  isLoading?: boolean;
}

export const ConsumedVoucherModal = ({
  visible,
  onHandleClose,
  title,
  consumedVouchersList,
  isLoading
}: Props) => {
  const snackbar = useSnackBar()
  const classes = useStyles();
    const navigate = useNavigate();
  
  const onCloseInternal = () => {
    onHandleClose();
  };

  const onTransactionNumberPress = (transactionNumber: string | undefined) => {
    navigate(`/app/transaction/${transactionNumber}`);
  };

  const onClickExport = useCallback(async () => {
    if (!consumedVouchersList?.[0]) {
      console.error('No data found for exporting');
      snackbar.show({
        severity: 'error',
        message: 'No data found for exporting'
      });
      return;
    }

    const formattedCSV = consumedVouchersList.map((i) => ({
      'Transaction Number': i.transaction_no ?? '',
      'Transaction Date':i.transaction_date ?? '',
      'Customer Name': i.customer_name ?? '',
      'Amount': i.amount ?? '',
      'Voucher Discount Amount':i.voucher_discount_amount ?? '',
      'Incentive Percentage':i.incentive_percentage ?? '',
      'Earnings':i.earnings ?? ''
    }));


    downloadCSV(formattedCSV, 'ConsumedVoucherEarnings.csv');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumedVouchersList]);

  return (
    <Modal
      disableBackdropClick
      open={visible}
      onClose={onCloseInternal}
      className={classes.modal}
    >
      <div className={classes.body}>
        {title && (
          <div className={classes.titleContainer}>
            <Typography variant="h5" color="textPrimary">
              {title}
            </Typography>
          </div>
        )}
        {!isLoading ? (
          <ScrollBar className={classes.formContainer}>
            {consumedVouchersList?.map((transaction, ind) => (
              <Card elevation={3} key={ind} style={{ marginBottom: 2 }}>
                <CardHeader
                  title={
                    <Typography
                      style={{ color: 'green', fontWeight: 'bolder' }}
                    >
                      🪙 Affiliate Earnings: +
                      {formatCurrency(
                        Number(
                          transaction?.earnings ?? '0'.replace(/[^0-9.-]+/g, '')
                        )
                      )}
                    </Typography>
                  }
                  subheader={
                    <div>
                      <Typography>
                        Transaction Amount:{' '}
                        {formatCurrency(transaction?.amount ?? '0')} |
                        Discounted:{' '}
                        {formatCurrency(
                          transaction?.voucher_discount_amount ?? '0'
                        )}{' '}
                        | Percentage:{' '}
                        {parseFloat(transaction?.incentive_percentage ?? '0') *
                          100}{' '}
                        %
                      </Typography>

                      <Typography style={{ display: 'block' }}>
                        Transaction Date: {transaction?.transaction_date}
                      </Typography>
                      <Typography style={{ fontWeight: 'bold' }}>
                        👤 Customer: {transaction?.customer_name} |{' '}
                        <Typography
                          component="span"
                          style={{
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            onTransactionNumberPress(transaction?.transaction_no)
                          }
                        >
                          {transaction?.transaction_no}
                        </Typography>
                      </Typography>
                    </div>
                  }
                />
              </Card>
            ))}
          </ScrollBar>
        ) : (
          <LinearProgress />
        )}

        <Box className={classes.footer}>
          <Button
            onClick={onClickExport}
            color="primary"
            variant="contained"
            className={classes.footerBtn}
          >
            Export
          </Button>
          <Button
            onClick={onCloseInternal}
            color="primary"
            variant="outlined"
            className={classes.footerBtn}
          >
            Close
          </Button>
        </Box>
      </div>
    </Modal>
  );
};
